/**
 * WorkdayReminderModal.js
 *
 * End-of-workday reminder (target reached, default 8 h): a centred premium card (NOT full screen, maxWidth ~420,
 * radius 28, solid surface, dim backdrop) with a Lottie alarm, the worked-vs-target ring, the running task-timer card with
 * a "stop it too" checkbox (default on), the primary "Munkanap lezárása" and the 15 / 30 / 60 minute snooze segments.
 * Mounted globally by `WorkdayOverlay` so it appears over every module.
 *
 * @module components/workday/WorkdayReminderModal
 */

import React, {useEffect, useState} from 'react';
import {Pressable, StyleSheet, Text, View} from 'react-native';
import LottieView from 'lottie-react-native';
import {useTranslation} from 'react-i18next';
import Icon from '../management/SafeIcon';
import {WorkdayCardShell} from './WorkdayShell';
import WorkdayRing from './WorkdayRing';
import {PrimaryButton, SnoozeSegments} from './WorkdayParts';
import {BRAND, useManagementTheme, withAlpha} from '../../helpers/managementTheme';
import {useWorkday, useWorkdayLive} from '../../helpers/WorkdayContext';
import {formatHM, SNOOZE_OPTIONS} from '../../helpers/workdayRules';

/**
 * @returns {JSX.Element|null} the reminder modal (rendered while `surface === 'reminder'`).
 */
export default function WorkdayReminderModal() {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const {surface, state, busy, stop, snooze, closeReminder} = useWorkday();
    const live = useWorkdayLive(1000);
    const [stopTimer, setStopTimer] = useState(true);
    const timerRunning = !!state?.timer?.running;
    const visible = surface === 'reminder' && !!state?.running;

    useEffect(() => {
        if (visible) {
            setStopTimer(true);
        }
    }, [visible]);

    if (!visible) {
        return null;
    }

    const options = state.settings.snoozeOptions?.length ? state.settings.snoozeOptions : SNOOZE_OPTIONS;

    return (
        <WorkdayCardShell visible onClose={closeReminder}>
            <View style={styles.top}>
                <View style={[styles.lottieWrap, {backgroundColor: withAlpha(BRAND.base, m.isDark ? 0.18 : 0.1)}]}>
                    <LottieView source={require('../../../assets/animations/workday-alarm.json')} autoPlay loop style={styles.lottie} />
                </View>
                <Pressable onPress={closeReminder} hitSlop={10} accessibilityRole="button" accessibilityLabel={t('myday_close')} style={[styles.close, {backgroundColor: m.colors.surfaceAlt}]}>
                    <Icon name="close" size={18} color={m.colors.subText} />
                </Pressable>
            </View>

            <View style={styles.body}>
                <Text style={[styles.title, {color: m.colors.heading}]}>{t('workday_reminder_title')}</Text>
                <Text style={[styles.text, {color: m.colors.subText}]}>{t('workday_reminder_text', {target: formatHM(live.targetSec)})}</Text>

                <View style={styles.ringRow}>
                    <WorkdayRing size={116} stroke={10} progress={live.progress} tone={live.tone} trackColor={withAlpha(m.colors.subText, 0.2)}>
                        <Text style={[styles.ringTime, {color: m.colors.heading}]} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.7}>{formatHM(live.workedSec)}</Text>
                        <Text style={[styles.ringSub, {color: m.colors.subText}]} numberOfLines={1}>{t('workday_target', {time: formatHM(live.targetSec)})}</Text>
                    </WorkdayRing>
                </View>

                {timerRunning && (
                    <Pressable
                        onPress={() => setStopTimer((v) => !v)}
                        accessibilityRole="checkbox"
                        accessibilityState={{checked: stopTimer}}
                        style={[styles.timerCard, {backgroundColor: m.colors.surfaceAlt, borderColor: m.colors.borderSoft}]}
                    >
                        <View style={[styles.timerIcon, {backgroundColor: withAlpha(m.colors.info, m.isDark ? 0.24 : 0.14)}]}>
                            <Icon name="timer-outline" size={18} color={m.colors.info} />
                        </View>
                        <View style={styles.timerText}>
                            <Text style={[styles.timerTitle, {color: m.colors.heading}]} numberOfLines={1}>{state.timer.note || t('workday_running_timer')}</Text>
                            <Text style={[styles.timerSub, {color: m.colors.subText}]} numberOfLines={2}>{t('workday_stop_timer_too')}</Text>
                        </View>
                        <View style={[styles.check, stopTimer ? {backgroundColor: BRAND.base, borderColor: BRAND.base} : {borderColor: m.colors.border}]}>
                            {stopTimer && <Icon name="check" size={16} color="#FFFFFF" />}
                        </View>
                    </Pressable>
                )}

                <PrimaryButton label={t('workday_stop')} icon="stop-circle-outline" busy={busy} onPress={() => stop({stopTimers: timerRunning && stopTimer})} />

                <Text style={[styles.snoozeTitle, {color: m.colors.subText}]}>{t('workday_snooze_title')}</Text>
                <SnoozeSegments options={options} disabled={busy} onPick={snooze} />
            </View>
        </WorkdayCardShell>
    );
}

const styles = StyleSheet.create({
    top: {alignItems: 'center', paddingTop: 18},
    lottieWrap: {width: 84, height: 84, borderRadius: 42, alignItems: 'center', justifyContent: 'center', overflow: 'hidden'},
    lottie: {width: 84, height: 84},
    close: {position: 'absolute', top: 14, right: 14, width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center'},
    body: {paddingHorizontal: 22, paddingTop: 12, paddingBottom: 22, gap: 12},
    title: {fontSize: 22, fontWeight: '900', letterSpacing: -0.4, textAlign: 'center'},
    text: {fontSize: 14, fontWeight: '600', textAlign: 'center', lineHeight: 20},
    ringRow: {alignItems: 'center', marginVertical: 4},
    ringTime: {fontSize: 24, fontWeight: '900', fontVariant: ['tabular-nums'], maxWidth: 84},
    ringSub: {fontSize: 11, fontWeight: '700', marginTop: 1},
    timerCard: {flexDirection: 'row', alignItems: 'center', gap: 10, borderRadius: 18, borderWidth: 1, padding: 12},
    timerIcon: {width: 36, height: 36, borderRadius: 12, alignItems: 'center', justifyContent: 'center'},
    timerText: {flex: 1},
    timerTitle: {fontSize: 14, fontWeight: '800'},
    timerSub: {fontSize: 12, fontWeight: '600', marginTop: 1},
    check: {width: 24, height: 24, borderRadius: 8, borderWidth: 2, alignItems: 'center', justifyContent: 'center'},
    snoozeTitle: {fontSize: 12.5, fontWeight: '800', textAlign: 'center', marginTop: 2},
});
